// 앱 메뉴(사이드바)가 화면에 두 벌 보이는 상태를 감지해 한 벌로 되돌리기 위한 순수 함수 모음.
// 🔴 «DOM 에 몇 개»가 아니라 «화면에 보이는 게 몇 개»를 센다 — keep-alive 사본이 DOM 에 남는 건 정상 구조다.
// 🔴 복원은 «우리가 숨긴 것»만 «숨기기 전 값»으로 — aside 는 인라인 display:'flex' 라 '' 로 덮으면 배치가 깨진다.
// 🔴 DOM 은 전부 인자로 받는다 — iframe 안 문서도 같은 함수로 본다.
export const APP_MENU_SELECTOR = 'aside[aria-label="앱 메뉴"]';
export const HIDDEN_ATTR = 'data-gw-sidebar-dup-hidden';

function viewOf(el, win) {
  if (win) return win;
  try { return (el && el.ownerDocument && el.ownerDocument.defaultView) || null; } catch (e) { return null; }
}

function ancestorMatches(el, selector) {
  try { return !!(el && typeof el.closest === 'function' && el.closest(selector)); } catch (e) { return false; }
}

function leftOf(el) {
  try {
    const rect = el.getBoundingClientRect();
    return Number.isFinite(rect && rect.left) ? rect.left : Number.MAX_SAFE_INTEGER;
  } catch (e) { return Number.MAX_SAFE_INTEGER; }
}

// 렌더 박스가 없거나(getClientRects 빈 목록) 크기 0·뷰포트 밖·visibility:hidden·display:none 이면 «안 보이는 것».
export function isVisible(el, win) {
  if (!el || typeof el.getClientRects !== 'function') return false;
  try {
    if (el.isConnected === false) return false;
    const rects = el.getClientRects();
    if (!rects || rects.length === 0) return false;
    const rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return false;
    const view = viewOf(el, win);
    if (!view) return false;
    const vw = Number.isFinite(view.innerWidth) ? view.innerWidth : 0;
    const vh = Number.isFinite(view.innerHeight) ? view.innerHeight : 0;
    if (vw > 0 && vh > 0 && (rect.right <= 0 || rect.bottom <= 0 || rect.left >= vw || rect.top >= vh)) return false;
    const style = typeof view.getComputedStyle === 'function' ? view.getComputedStyle(el) : null;
    if (style && (style.visibility === 'hidden' || style.display === 'none')) return false;
    return true;
  } catch (e) { return false; }
}

// 표식 없이 «보이는» 앱 메뉴 목록 — 우리가 숨긴 것은 세지 않는다.
export function countVisible(doc, win) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return [];
  let nodes;
  try { nodes = doc.querySelectorAll(APP_MENU_SELECTOR); } catch (e) { return []; }
  const view = win || doc.defaultView || null;
  const out = [];
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i];
    if (el && typeof el.hasAttribute === 'function' && el.hasAttribute(HIDDEN_ATTR)) continue;
    if (isVisible(el, view)) out.push(el);
  }
  return out;
}

// ① 임베드 앱이 떠 있으면 main 슬롯 안의 것 ② 아니면 임베드 슬롯 밖의 것 ③ 둘 다 없으면 가장 왼쪽.
export function pickKeeper(visibleEls, activeEmbedApp) {
  const list = (Array.isArray(visibleEls) ? visibleEls : []).filter(Boolean);
  if (list.length === 0) return null;
  if (activeEmbedApp) {
    const inMain = list.find((el) => ancestorMatches(el, '[data-gw-embed-slot="main"]'));
    if (inMain) return inMain;
  }
  const outsideSlot = list.find((el) => !ancestorMatches(el, '[data-gw-embed-slot]'));
  if (outsideSlot) return outsideSlot;
  return list.reduce((best, el) => (leftOf(el) < leftOf(best) ? el : best), list[0]);
}

// 숨기기 전 style.display 를 표식 속성에 보관한다(복원은 그 값으로만).
export function hideOthers(others) {
  const hidden = [];
  (Array.isArray(others) ? others : []).filter(Boolean).forEach((el) => {
    try {
      if (!el.style || typeof el.setAttribute !== 'function') return;
      if (typeof el.hasAttribute === 'function' && el.hasAttribute(HIDDEN_ATTR)) return;
      el.setAttribute(HIDDEN_ATTR, el.style.display || '');
      el.style.display = 'none';
      hidden.push(el);
    } catch (e) { /* 한 벌이 실패해도 나머지는 계속 */ }
  });
  return hidden;
}

export function hasHidden(doc) {
  if (!doc || typeof doc.querySelector !== 'function') return false;
  try { return !!doc.querySelector('[' + HIDDEN_ATTR + ']'); } catch (e) { return false; }
}

// 표식 붙은 것만 보관값으로 되돌린다. 표식 없는 요소(원래부터 display:none 인 사본)는 건드리지 않는다.
export function restoreHidden(doc) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return [];
  let nodes;
  try { nodes = doc.querySelectorAll('[' + HIDDEN_ATTR + ']'); } catch (e) { return []; }
  const restored = [];
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i];
    try {
      const saved = el.getAttribute(HIDDEN_ATTR);
      if (el.style) el.style.display = saved === null ? '' : saved;
      el.removeAttribute(HIDDEN_ATTR);
      restored.push(el);
    } catch (e) { /* 한 벌이 실패해도 나머지는 계속 */ }
  }
  return restored;
}

// iframe 안에 게이트웨이가 통째로 다시 떴는가. contentDocument 가 null 이거나 접근이 예외면 false.
export function findNestedAppMenu(iframeEl) {
  if (!iframeEl) return false;
  try {
    const doc = iframeEl.contentDocument;
    if (!doc || typeof doc.querySelectorAll !== 'function') return false;
    const win = iframeEl.contentWindow || doc.defaultView || null;
    const nodes = doc.querySelectorAll(APP_MENU_SELECTOR);
    for (let i = 0; i < nodes.length; i += 1) {
      if (isVisible(nodes[i], win)) return true;
    }
    return false;
  } catch (e) { return false; }
}
